import {useContext, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {shallowEqual, useSelector} from 'react-redux'
import {Redirect, useHistory, useParams} from 'react-router-dom'
import {RootState} from '../../../setup'
import {useLayout} from '../../../_metronic/layout/core'
import {UserRole} from '../../constants/roles'
import {showErrorAlert} from '../../helpers/alert'
import {ConfirmContext} from '../../helpers/alertConfirm'
import {LoaderOverlay} from '../../hooks/loader/LoaderOverlay'
import {UserModel} from '../../modules/auth/models/UserModel'
import {handleApiError} from '../../utils/handleApiError'
import {hasRole} from '../../utils/permissions'
import Pagination from '../components/Pagination'
import {CatalogAdminTable} from './components/CatalogAdminTable'
import {CatalogFormModal} from './components/CatalogFormModal'
import {CatalogPicker} from './components/CatalogPicker'
import {CatalogReorderModal} from './components/CatalogReorderModal'
import {catalogConfigs, getCatalogConfig} from './configs'
import {useCatalogAdmin} from './hooks/useCatalogAdmin'
import {CatalogConfig, CatalogItem} from './types'
import {hasSearch, isItemActive, StatusFilter} from './utils/catalogFilter'
import {buildCsv, nextSort, Sort} from './utils/serverTable'

const DEFAULT_PAGE_SIZE = 10

// Trigger a client-side download of a CSV string (server-side export only).
const downloadCsv = (filename: string, csv: string) => {
  // Prepend a UTF-8 BOM so Excel opens accented values correctly.
  const blob = new Blob(['﻿', csv], {type: 'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Generic catalog admin screen, driven entirely by a CatalogConfig. Adding a catalog in Phase B
// = one config + one menu entry; this screen is reused as-is. Role-gated to ADMIN, mirroring the
// community-admin screen (layout-chrome toggle + redirect-on-deny).
const CatalogAdminScreen = ({config}: {config: CatalogConfig}) => {
  const intl = useIntl()
  const history = useHistory()
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const {config: layoutConfig, setLayout} = useLayout()
  const restoreRef = useRef(layoutConfig)
  const confirmCtx = useContext(ConfirmContext)

  const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE
  const itemLabel = intl.formatMessage({id: config.singularId})
  // Read-only catalogs (no write API) omit the Add button and per-row actions (S2).
  const writable = config.writable !== false
  const showSearch = hasSearch(config)
  // Status column + active/inactive filter + deactivate only exist with a soft-delete field.
  const showStatusFilter = Boolean(config.softDelete)
  // CSV export is a server-side-only affordance, opted into per catalog.
  const showExportCsv = Boolean(config.serverSide?.csvExport)
  // Names a specific row in confirm dialogs / toasts; falls back to the singular noun.
  const itemName = (item: CatalogItem): string => String(item[config.primaryField] ?? itemLabel)

  const [search, setSearch] = useState('')
  // Debounced copy of `search` — this is what drives the data query, so a server-side catalog
  // fires one request per typing pause instead of one per keystroke. `search` (immediate) still
  // drives the input and the reset-to-page-1 effect, so a search from page N snaps to page 1
  // before the debounced request fires (never a stale-offset empty page).
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  // Server-side sort (null → the backend's configured default ordering). Ignored client-side.
  const [sort, setSort] = useState<Sort | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [reorderOpen, setReorderOpen] = useState(false)
  const [selected, setSelected] = useState<CatalogItem | undefined>(undefined)
  const [exporting, setExporting] = useState(false)
  const showReorder = Boolean(config.reorderable) && writable

  const query = {search: debouncedSearch, status, sort, page: currentPage, pageSize}
  const {items, totalPages, loading, error, truncated, reload, service, fetchAllForExport} =
    useCatalogAdmin(config, query)

  useEffect(() => {
    restoreRef.current = layoutConfig
    setLayout({
      ...layoutConfig,
      header: {...layoutConfig.header, display: true},
      aside: {...layoutConfig.aside, display: true},
      toolbar: {...layoutConfig.toolbar, display: false},
      footer: {...layoutConfig.footer, display: true},
    })

    if (!hasRole(user, UserRole.ADMIN)) {
      history.goBack()
      showErrorAlert({
        title: intl.formatMessage({id: 'CATALOG.ACCESS_DENIED.TITLE'}),
        message: intl.formatMessage({id: 'CATALOG.ACCESS_DENIED.MESSAGE'}),
      })
    }

    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A changed filter/search/sort resets to the first page.
  useEffect(() => {
    setCurrentPage(1)
  }, [search, status, sort])

  // Switching catalog via the picker (or a deep-link URL change) must clear per-catalog state so
  // a leftover search / sort / modal from the previous catalog does not leak into the new one.
  useEffect(() => {
    setSearch('')
    setDebouncedSearch('')
    setStatus('all')
    setSort(null)
    setCurrentPage(1)
    setModalOpen(false)
    setReorderOpen(false)
    setSelected(undefined)
  }, [config.slug])

  // Debounce the search box: only the settled value drives the data query (300ms after the last
  // keystroke). A no-op for client-side catalogs (they filter in memory); the point is one request
  // per pause for server-side catalogs.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Keep the page within range — e.g. after deactivating the last row on the last server page.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const onSort = (field: string) => setSort((current) => nextSort(current, field))

  const handleExportCsv = async () => {
    try {
      setExporting(true)
      const rows = await fetchAllForExport()
      const columns = config.fields.filter((field) => field.showInTable)
      const headers = columns.map((field) => intl.formatMessage({id: field.labelId}))
      if (config.softDelete) headers.push(intl.formatMessage({id: 'CATALOG.COLUMN.STATUS'}))
      const csvRows = rows.map((item) => {
        const cells: unknown[] = columns.map((field) => item[field.name] ?? '')
        if (config.softDelete) {
          cells.push(
            intl.formatMessage({
              id: isItemActive(item, config) ? 'CATALOG.STATUS.ACTIVE' : 'CATALOG.STATUS.INACTIVE',
            })
          )
        }
        return cells
      })
      downloadCsv(`${config.slug}.csv`, buildCsv(headers, csvRows))
    } catch (err) {
      handleApiError(err, {})
    } finally {
      setExporting(false)
    }
  }

  const openCreate = () => {
    setSelected(undefined)
    setModalOpen(true)
  }

  const openEdit = (item: CatalogItem) => {
    setSelected(item)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelected(undefined)
  }

  const handleSubmit = async (values: Record<string, string>) => {
    try {
      if (selected) {
        await service.update(selected.id, values)
      } else {
        await service.create(values)
      }
      closeModal()
      await reload()
      showErrorAlert({
        title: intl.formatMessage({id: 'CATALOG.TOAST.SUCCESS_TITLE'}),
        message: intl.formatMessage(
          {id: selected ? 'CATALOG.TOAST.UPDATED' : 'CATALOG.TOAST.CREATED'},
          {item: itemLabel}
        ),
      })
    } catch (err) {
      handleApiError(err, {})
    }
  }

  const handleToggleActive = async (item: CatalogItem) => {
    if (!config.softDelete) return
    const active = isItemActive(item, config)
    const name = itemName(item)
    const confirmed = await confirmCtx?.confirm({
      title: intl.formatMessage({
        id: active ? 'CATALOG.CONFIRM.DEACTIVATE_TITLE' : 'CATALOG.CONFIRM.REACTIVATE_TITLE',
      }),
      message: intl.formatMessage(
        {id: active ? 'CATALOG.CONFIRM.DEACTIVATE_MESSAGE' : 'CATALOG.CONFIRM.REACTIVATE_MESSAGE'},
        {name}
      ),
      btnTextAction: intl.formatMessage({
        id: active ? 'CATALOG.ACTION.DEACTIVATE' : 'CATALOG.ACTION.REACTIVATE',
      }),
      btnTextDecline: intl.formatMessage({id: 'CATALOG.MODAL.CANCEL'}),
    })
    if (!confirmed) return

    try {
      // Set the model's soft-delete field to its inactive/active value (D1: honor the model's
      // own convention; never hard delete).
      const target = active ? !config.softDelete.activeValue : config.softDelete.activeValue
      await service.update(item.id, {[config.softDelete.field]: target})
      await reload()
      showErrorAlert({
        title: intl.formatMessage({id: 'CATALOG.TOAST.SUCCESS_TITLE'}),
        message: intl.formatMessage(
          {id: active ? 'CATALOG.TOAST.DEACTIVATED' : 'CATALOG.TOAST.REACTIVATED'},
          {item: name}
        ),
      })
    } catch (err) {
      handleApiError(err, {})
    }
  }

  return (
    <>
      <LoaderOverlay visible={loading} message={intl.formatMessage({id: 'CATALOG.LOADING'})} />

      {modalOpen && (
        <CatalogFormModal
          config={config}
          item={selected}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      )}

      {reorderOpen && (
        <CatalogReorderModal
          config={config}
          onClose={() => setReorderOpen(false)}
          onSaved={reload}
        />
      )}

      {/* nb-catalog-admin: catalog-only scoping hook (the .admin-community__* classes are shared
          with the community-admin and suggestions screens, so styles are scoped under this class
          to avoid leaking there). Adds no layout of its own. */}
      <div className='nb-catalog-admin admin-community__wrapper'>
        {/* Back link — same idiom as the sibling admin screen members/MenmberDetailPage.tsx. */}
        <div className='help-center-sections w-100 tap-flex-center justify-content-start mb-5'>
          <div
            className='nb-link-underline-500 w-auto cursor-pointer'
            onClick={() => history.goBack()}
          >
            Back
          </div>
        </div>

        <CatalogPicker
          slug={config.slug}
          onChange={(nextSlug) => history.push(`/admin/catalog/${nextSlug}`)}
        />

        <h3>{intl.formatMessage({id: config.titleId})}</h3>

        <div className='admin-community__list_wrap'>
          {truncated && (
            <div className='alert alert-warning' role='alert'>
              {intl.formatMessage({id: 'CATALOG.TRUNCATED'}, {limit: config.fetchLimit ?? 1000})}
            </div>
          )}

          <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5'>
            {showSearch ? (
              <div className='search_input'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z'
                    stroke='#C0C0C0'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M17.5 17.5L13.875 13.875'
                    stroke='#C0C0C0'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <input
                  type='text'
                  placeholder={intl.formatMessage({id: 'CATALOG.SEARCH.PLACEHOLDER'})}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            ) : (
              <div />
            )}

            <div className='d-flex align-items-center gap-3'>
              {showStatusFilter && (
                <select
                  className='form-select w-auto'
                  value={status}
                  onChange={(e) => setStatus(e.target.value as StatusFilter)}
                  aria-label={intl.formatMessage({id: 'CATALOG.COLUMN.STATUS'})}
                >
                  <option value='all'>{intl.formatMessage({id: 'CATALOG.FILTER.ALL'})}</option>
                  <option value='active'>
                    {intl.formatMessage({id: 'CATALOG.FILTER.ACTIVE'})}
                  </option>
                  <option value='inactive'>
                    {intl.formatMessage({id: 'CATALOG.FILTER.INACTIVE'})}
                  </option>
                </select>
              )}

              {showExportCsv && (
                <button
                  className='btn btn-outline btn-outline-dark'
                  onClick={handleExportCsv}
                  disabled={exporting || loading}
                >
                  {intl.formatMessage({
                    id: exporting ? 'CATALOG.BUTTON.EXPORTING' : 'CATALOG.BUTTON.EXPORT_CSV',
                  })}
                </button>
              )}

              {showReorder && (
                <button
                  type='button'
                  className='btn btn-outline btn-outline-dark'
                  onClick={() => setReorderOpen(true)}
                  disabled={loading}
                >
                  {intl.formatMessage({id: 'CATALOG.BUTTON.REORDER'})}
                </button>
              )}

              {writable && (
                <button className='tap-add-experience-2-btn-secondary' onClick={openCreate}>
                  <span>
                    &nbsp;&nbsp;{intl.formatMessage({id: 'CATALOG.BUTTON.ADD'}, {item: itemLabel})}
                  </span>
                </button>
              )}
            </div>
          </div>

          {!writable && (
            <div className='text-muted mb-5'>
              {intl.formatMessage({id: config.readonlyNoteId ?? 'CATALOG.READONLY_NOTE'})}
            </div>
          )}

          {error ? (
            <div className='text-center py-10'>{intl.formatMessage({id: 'CATALOG.ERROR'})}</div>
          ) : !loading && items.length === 0 ? (
            <div className='text-center py-10'>{intl.formatMessage({id: 'CATALOG.EMPTY'})}</div>
          ) : (
            <CatalogAdminTable
              config={config}
              items={items}
              writable={writable}
              sort={sort}
              onSort={onSort}
              onEdit={openEdit}
              onToggleActive={handleToggleActive}
            />
          )}

          {!error && items.length > 0 && (
            <div className='tap-flex-center mt-5'>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// Route entry: resolve the slug to a config. `/admin/catalog` (no slug) redirects to the first
// registered catalog; an unknown slug 404s. Keeps hooks unconditional in the screen.
const CatalogAdminPage = () => {
  const {slug} = useParams<{slug?: string}>()

  if (!slug) {
    const first = catalogConfigs[0]
    return <Redirect to={`/admin/catalog/${first.slug}`} />
  }

  const config = getCatalogConfig(slug)

  if (!config) {
    return <Redirect to='/error/404' />
  }

  return <CatalogAdminScreen config={config} />
}

export default CatalogAdminPage
